import * as React from 'react'
import moment from 'moment'

import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { message } from 'antd'
import { InventoryLogAPI } from '@getgreenline/inventory'
import {
  InventoryLogType,
  IInventoryLog,
  IInventoryLogFilter,
} from '@getgreenline/inventory/build/models/inventoryLog'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { parseErrorMsg, parseAndDownloadCSV } from '../../utilities/helpers'
import { DateFilter } from '../../containers/Dashboard/DashboardReports/Financial/ReportFilter/DateFilter'
import { BlazeButton } from '../CustomButtons/BlazeButton'
import './ExportInventoryLogsButton.scss'
import { Track } from '../../Track'
import dayjs from 'dayjs'

interface IProps {
  currentCompanyStore?: CurrentCompanyStore

  locationId: number | undefined
  employeeIds: string[]
  logType: InventoryLogType | undefined
  productIds: string[]
}

interface IModalState {
  isExportModalOpen: boolean
  startDate: Date | null
  endDate: Date | null
  focusedInput: 'startDate' | 'endDate' | null
}

interface IState extends IModalState {
  isLoading: boolean
}

const modalDefaultState: IModalState = {
  isExportModalOpen: false,
  startDate: null,
  endDate: null,
  focusedInput: null,
}

@inject('currentCompanyStore')
@observer
export class ExportInventoryLogsButton extends React.Component<IProps, IState> {
  INVENTORYLOG_LIMIT = 300

  constructor(props: IProps) {
    super(props)
    this.state = {
      isLoading: false,
      ...modalDefaultState,
    }
  }

  openExportModal = () => {
    this.setState({ isExportModalOpen: true })
  }

  closeExportModal = () => {
    this.setState({
      ...modalDefaultState,
    })
  }

  createInventoryJSON = async (filter: IInventoryLogFilter) => {
    const company = this.props.currentCompanyStore!.company!

    // Get first set of longs
    const paginatedInvetoryLogs = await InventoryLogAPI.getInventoryLogs(company.id, filter)

    // Store the inventory logs
    let inventoryLogs = [...paginatedInvetoryLogs.data]

    // Track the length of received and the offset
    let receivedLength = paginatedInvetoryLogs.data.length
    filter.offset = this.INVENTORYLOG_LIMIT

    // Whilst we have more or equal receivedLength than the limit, keep getting more longs
    while (receivedLength >= this.INVENTORYLOG_LIMIT) {
      const additionalInvetoryLogs = await InventoryLogAPI.getInventoryLogs(company.id, filter)

      filter.offset += this.INVENTORYLOG_LIMIT
      receivedLength = additionalInvetoryLogs.data.length
      inventoryLogs = [...inventoryLogs, ...additionalInvetoryLogs.data]
    }

    return this.convertInventoryLogForCSV(inventoryLogs)
  }

  convertInventoryLogForCSV = (logs: IInventoryLog[]) => {
    return logs.map((log) => ({
      Date: moment(log.createDate).format('YYYY-MM-DD HH:mm:ss'),
      Type: log.type,
      SKU: log.productSku,
      Barcode: log.productBarcode,
      Product: log.productName,
      Employee: log.userName,
      Location: log.entityName,
      Opening: log.previousValue,
      Change: log.updateValue,
      Closing: log.newValue,
      Notes: log.note,
    }))
  }

  get validTimeRange() {
    const { startDate, endDate } = this.state
    if (!startDate || !endDate) return false
    return moment(endDate).diff(startDate, 'month', true) < 1
  }

  export = async () => {
    const { startDate, endDate } = this.state
    try {
      if (!startDate || !endDate) {
        throw new Error('Date range is required')
      }

      if (!this.validTimeRange) {
        throw new Error('Maximum date range is 1 month')
      }

      const params = {
        limit: this.INVENTORYLOG_LIMIT,
        offset: 0,
        locationId: this.props.locationId,
        employeeIds: this.props.employeeIds,
        productIds: this.props.productIds,
        type: this.props.logType,
        startDate,
        endDate,
      }

      await this.setState({ isLoading: true })

      Track.track('dashboard:inventoryLogs_exported', params)

      const inventoryJSON = await this.createInventoryJSON(params)

      parseAndDownloadCSV('inventory-logs', inventoryJSON)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error exporting your logs')
    } finally {
      this.setState({
        isLoading: false,
        ...modalDefaultState,
      })
    }
  }

  render() {
    const { isLoading, isExportModalOpen, startDate, endDate } = this.state
    const exportLabel =
      !startDate || !endDate
        ? 'Set a date range'
        : this.validTimeRange
        ? 'Export as csv'
        : 'Maximum date range 1 month'

    return (
      <>
        <BlazeButton
          buttonProps={{
            icon: 'download',
            loading: isLoading,
            disabled: isLoading,
            onClick: this.openExportModal,
          }}
          titleProps={{
            title: 'Export',
          }}
        />

        <Modal isOpen={isExportModalOpen}>
          <ModalHeader toggle={this.closeExportModal}>
            <div>Export inventory logs</div>
          </ModalHeader>
          <ModalBody>
            <div id='export-inventory-logs-buttons-modal'>
              <div className='text-muted text-left'>Select date range</div>
              <div className='mt-3 mb-3'>
                <DateFilter
                  startDate={startDate || null}
                  endDate={endDate || null}
                  onStartDateChange={(date) => {
                    this.setState({
                      startDate: date ? dayjs(date).startOf('day').toDate() : null,
                    })
                  }}
                  onEndDateChange={(date) => {
                    this.setState({
                      endDate: date ? dayjs(date).endOf('day').toDate() : null,
                    })
                  }}
                  maxRangeMonths={12}
                />
              </div>

              <BlazeButton
                buttonProps={{
                  type: 'primary',
                  loading: isLoading,
                  disabled: isLoading || !this.validTimeRange,
                  onClick: this.export,
                }}
                titleProps={{
                  title: exportLabel,
                }}
              />
            </div>
          </ModalBody>
        </Modal>
      </>
    )
  }
}
